import React from "react";
import PropTypes from "prop-types";

// Minimum CSS size (px) before we bother sizing the backing buffer. Below this
// the box is mid-layout and getBoundingClientRect is not yet meaningful.
const MIN_SIZE = 100;
// Ignore sub-pixel ResizeObserver jitter to avoid re-setup thrash / RO loops.
const SIZE_EPS = 1;

const Canvas = ({ draw }) => {
  const canvas = React.useRef();
  // Bumped on every successful (re)setup so the render effect redraws after the
  // backing buffer is resized (a resize clears the canvas). 0 = not yet set up.
  const [setupVersion, setSetupVersion] = React.useState(0);

  // Initialize the backing buffer AND re-setup on meaningful size changes. The
  // stage aspect changes once on stream connect (4/3 → real) and again on window
  // resize, so — unlike the old one-time `isSetup` lock — the buffer must follow.
  React.useEffect(() => {
    const canvasEl = canvas.current;
    if (!canvasEl) return;

    const context = canvasEl.getContext("2d");
    let lastW = 0;
    let lastH = 0;

    const setupCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      // Use the LAYOUT size (clientWidth/Height), NOT getBoundingClientRect():
      // the latter returns the element's transform-scaled visual size, so while the
      // Ant Modal plays its open zoom (a CSS transform) it reports a transient,
      // smaller size. Sizing the backing buffer from that — then CSS-stretching the
      // canvas to 100% — scaled the drawn lineWidth by finalBox/transientBox, which
      // varied per open. clientWidth is immune to transforms (final layout size).
      const cssW = canvasEl.clientWidth;
      const cssH = canvasEl.clientHeight;

      // Wait until the box has a sane size (video stage should be bigger).
      if (cssW < MIN_SIZE || cssH < MIN_SIZE) {
        return false;
      }

      // Skip if the size has not changed meaningfully — guards against
      // ResizeObserver feedback loops and needless buffer churn.
      if (
        Math.abs(cssW - lastW) <= SIZE_EPS &&
        Math.abs(cssH - lastH) <= SIZE_EPS
      ) {
        return false;
      }

      lastW = cssW;
      lastH = cssH;

      // Size the backing buffer for the device pixel ratio (sharp lines).
      canvasEl.width = cssW * dpr;
      canvasEl.height = cssH * dpr;

      // Reset transform before scaling (this can be a re-setup) then re-scale.
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.scale(dpr, dpr);

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      // Trigger a redraw — the resize cleared the canvas.
      setSetupVersion((v) => v + 1);
      return true;
    };

    // Re-run setup whenever the observed box changes (threshold/size guarded).
    const resizeObserver = new ResizeObserver(() => {
      setupCanvas();
    });
    resizeObserver.observe(canvasEl);

    // Also try an immediate setup for the common already-sized case.
    setupCanvas();

    return () => {
      resizeObserver.disconnect();
    };
  }, []); // Only wire up observers once on mount.

  // Render effect — redraws when the draw callback identity changes
  // (refreshKey / showDrawLines / draw1 / draw2) or after any (re)setup.
  React.useEffect(() => {
    const canvasEl = canvas.current;
    if (!canvasEl || setupVersion === 0) return;

    const context = canvasEl.getContext("2d");
    const frameId = window.requestAnimationFrame(() => draw(context));

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [draw, setupVersion]);

  return (
    <canvas
      ref={canvas}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        width: "100%",
        zIndex: 999,
      }}
    />
  );
};

Canvas.propTypes = {
  draw: PropTypes.func.isRequired,
};

export default Canvas;
